import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลผู้ใช้แบบย่อ
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// Interface สำหรับ Brand
export interface Brand {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_active: boolean | number | string | null; // widened to defensively accept BE inconsistency (0/1, '0'/'1')
  created_at: string;
  updated_at?: string;
  created_by_user_id?: number;
  created_by?: UserInfo;
  updated_by_user_id?: number;
  updated_by?: UserInfo;
}

// Interface สำหรับ Brand Form
export interface BrandForm {
  code: string;
  name: string;
  description?: string;
  is_active?: boolean;
}

// Interface สำหรับ Brand List Response
export interface BrandListResponse {
  items: Brand[];
  total: number;
  page: number;
  limit: number;
}

// Interface สำหรับ Brand Query Parameters
export interface GetBrandsParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

const brandService = {
  // ดึงรายการยี่ห้อทั้งหมด
  async getBrands(params?: GetBrandsParams): Promise<BrandListResponse> {
    try {
      const response = await ApiWithLogging.get('/brands/', 
        { params },
        {
          service: 'brandService',
          action: 'view_brands_list',
          entityType: 'brand'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  },

  // ดึงรายการยี่ห้อที่ใช้งานอยู่
  async getActiveBrands(): Promise<Brand[]> {
    try {
      const response = await ApiWithLogging.get('/brands/active', {},
        {
          service: 'brandService',
          action: 'view_active_brands',
          entityType: 'brand'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching active brands:', error);
      throw error;
    }
  },

  // ดึงข้อมูลยี่ห้อตาม ID
  async getBrandById(id: number): Promise<Brand> {
    try {
      const response = await ApiWithLogging.get(`/brands/${id}`, {},
        {
          service: 'brandService',
          action: 'view_brand_detail',
          entityType: 'brand',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching brand by ID:', error);
      throw error;
    }
  },

  // สร้างยี่ห้อใหม่
  async createBrand(brandData: BrandForm): Promise<Brand> {
    try {
      const response = await ApiWithLogging.post('/brands/', brandData, {},
        {
          service: 'brandService',
          action: 'create_brand',
          entityType: 'brand',
          details: { code: brandData.code, name: brandData.name }
        }
      );
      return response;
    } catch (error) {
      console.error('Error creating brand:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลยี่ห้อ
  // BUG-S162-P1: pass the record's last-seen updated_at as `ifMatch` to enable the backend
  // optimistic-concurrency lock (stale two-tab overwrite → 409). Optional.
  async updateBrand(id: number, brandData: Partial<BrandForm>, ifMatch?: string): Promise<Brand> {
    try {
      const response = await ApiWithLogging.put(`/brands/${id}`, brandData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'brandService',
          action: 'update_brand',
          entityType: 'brand',
          entityId: id,
          details: { updated_fields: Object.keys(brandData) }
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating brand:', error);
      throw error;
    }
  },

  // ลบยี่ห้อ
  async deleteBrand(id: number): Promise<void> {
    try {
      await ApiWithLogging.delete(`/brands/${id}`, {},
        {
          service: 'brandService',
          action: 'delete_brand',
          entityType: 'brand',
          entityId: id
        }
      );
    } catch (error) {
      console.error('Error deleting brand:', error);
      throw error;
    }
  },

  // เปิด/ปิดใช้งานยี่ห้อ (สลับอัตโนมัติ)
  async toggleBrandStatus(id: number): Promise<Brand> {
    try {
      const response = await ApiWithLogging.patch(`/brands/${id}/toggle`, {}, {},
        {
          service: 'brandService',
          action: 'toggle_brand_status',
          entityType: 'brand',
          entityId: id,
        }
      );
      return response;
    } catch (error) {
      console.error('Error toggling brand status:', error);
      throw error;
    }
  },

  // ส่งออกข้อมูลยี่ห้อเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก
  // จะได้ไฟล์ "ทั้งระบบ" โดยไม่มีอะไรเตือน (GAP-EXPORT-FILTER)
  // ชุดพารามิเตอร์ต้องตรงกับที่ list query ส่ง และตรงกับ signature ของ endpoint
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, any> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = params.is_active;

    const response = await ApiWithLogging.get('/brands/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'brandService',
        action: 'export_brands_excel',
        entityType: 'brand'
      }
    );
    return response;
  }
};

export default brandService;